import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Button } from "../components/ui/button";
import { BRAND, VISION, TRUST_BAR, SEGMENTS } from "../lib/data";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

const JOURNEY = [
  { year: "1994", title: "The Beginning", desc: "Vishal Gupta starts with door-to-door marketing — grit, hustle and a vision." },
  { year: "2005", title: "Finance & Loan Doctor", desc: "Genesis becomes a trusted name in loans, subsidies & project funding." },
  { year: "2015", title: "Agro & Eco Innovation", desc: "Pioneering cow-dung products, dairy and organic agriculture." },
  { year: "2024", title: "Multi-Sector Empire", desc: "Education, e-commerce, green energy, media & global trade." },
  { year: "2032", title: "World's Trusted Brand", desc: "A ₹1,25,000 Cr ecosystem — from village to global." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28 pb-20 bg-cream-100 text-soil-700 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">About Genesis</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-forest-800 leading-tight">From a Village Dream to a <span className="saffron-text">Multi-Sector Empire</span></h1>
            <p className="text-soil-600 mt-5">Founded in <strong className="text-forest-800">{BRAND.since}</strong>, {BRAND.full} ({BRAND.legal}) began with door-to-door marketing and a relentless belief in opportunity. Three decades later, we operate across finance, agriculture & eco-industry, education, green energy, e-commerce, media and global trade.</p>
            <p className="text-soil-500 mt-4">Known across India as the <strong className="text-terracotta-600">Loan Doctor</strong>, and pioneering the country's cow-dung innovation movement, Genesis exists to create opportunities — for farmers, students, entrepreneurs and investors alike.</p>
            <Link to="/businesses"><Button className="forest-gradient text-cream-50 font-bold h-12 px-7 rounded-full mt-7">Explore Our Businesses</Button></Link>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative rounded-3xl overflow-hidden border border-cream-300 h-[420px]">
            <img loading="lazy" decoding="async" src={SEGMENTS[0].img} alt="Genesis" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-800/60 to-transparent" />
          </motion.div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16 py-8 border-y border-cream-300">
          {TRUST_BAR.map((s) => (<div key={s.label} className="text-center"><div className="font-mono-fin text-2xl sm:text-3xl font-extrabold saffron-text">{s.value}</div><div className="text-sm text-soil-500 mt-1">{s.label}</div></div>))}
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[{ icon: "Eye", t: "Our Vision", d: VISION.vision }, { icon: "Target", t: "Our Mission", d: VISION.mission }, { icon: "TrendingUp", t: "Financial Vision", d: VISION.financialVision }].map((v) => (
            <div key={v.t} className="card-cream rounded-2xl p-7"><div className="w-12 h-12 rounded-xl bg-gold/12 border border-saffron-500/30 flex items-center justify-center mb-4"><Icon name={v.icon} className="w-6 h-6 text-terracotta-600" /></div><h3 className="font-heading text-xl font-semibold text-forest-800">{v.t}</h3><p className="text-soil-500 text-sm mt-2">{v.d}</p></div>
          ))}
        </div>

        <div className="mt-20">
          <SectionHeading overline="Our Journey" title="Three Decades of Building" />
          <div className="relative border-l-2 border-saffron-500/30 ml-3 space-y-8">
            {JOURNEY.map((j, i) => (
              <motion.div key={j.year} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="relative pl-8">
                <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full gold-gradient" />
                <div className="font-mono-fin text-terracotta-600 font-bold">{j.year}</div>
                <h3 className="font-heading text-lg font-semibold text-forest-800">{j.title}</h3>
                <p className="text-soil-500 text-sm">{j.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-20 card-cream rounded-3xl p-8 sm:p-10 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-1 text-center">
            <div className="w-32 h-32 rounded-full gold-gradient flex items-center justify-center mx-auto"><span className="font-heading text-5xl font-black text-cream-50">VG</span></div>
            <h3 className="font-heading text-xl font-bold text-forest-800 mt-4">{BRAND.contactPerson}</h3>
            <p className="text-terracotta-600 text-sm">{BRAND.designation}</p>
          </div>
          <div className="md:col-span-2">
            <Icons.Quote className="w-8 h-8 text-terracotta-500/40" />
            <p className="text-soil-700 text-lg mt-2 italic">"We build businesses, not just companies. From the smallest village to global markets — Genesis exists to create opportunities and uplift lives."</p>
            <p className="text-soil-400 text-sm mt-4">— {BRAND.contactPerson}, {BRAND.designation}, {BRAND.full}</p>
            <p className="text-terracotta-600 font-semibold mt-3">"Give Your Best up to the Last Breath." · "Do or Die"</p>
          </div>
        </div>
      </div>
    </div>
  );
}
